// C:\Users\HDP10\Desktop\Nueva carpeta (6)\Theblacklist-main\Theblacklist-main\src\components\ChatApp.js

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import {
  ChatBubbleLeftRightIcon, PaperAirplaneIcon, MagnifyingGlassIcon, XMarkIcon,
  UserGroupIcon, PlusIcon, ChevronLeftIcon, UsersIcon, HomeIcon, CheckCircleIcon,
  PhotoIcon, DocumentIcon, ArrowDownTrayIcon, EyeIcon, PlayIcon, ArrowUpIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import socketService from '../services/socket';
import toast from 'react-hot-toast';
import { ChatNotificationContext } from '../App';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const PAGE_SIZE = 50;

const TG = {
  bg: '#0e1621', sidebar: '#17212b', header: '#17212b', input: '#17212b',
  bubbleOwn: '#2b5278', bubbleOther: '#182533', text: '#ffffff', textMuted: '#7d8e98',
  accent: '#64b5ef', accentDark: '#5288c1', border: '#101921', divider: '#242f3d',
  online: '#4dcd5e', danger: '#e53935', dateBadge: 'rgba(24,37,51,0.85)', hover: '#202b36'
};

function ChatApp() {
  const { resetUnreadMessages } = useContext(ChatNotificationContext);

  // ============ ESTADOS ============
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagePage, setMessagePage] = useState(1);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchUsers, setSearchUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [typing, setTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [activeTab, setActiveTab] = useState('direct');
  const [chatFilter, setChatFilter] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageViewer, setImageViewer] = useState({ open: false, url: '', name: '' });

  // Panel info + añadir miembros
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [chatInfo, setChatInfo] = useState(null);
  const [infoTab, setInfoTab] = useState('members');
  const [chatMedia, setChatMedia] = useState({ images: [], files: [] });
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [addMembersSearch, setAddMembersSearch] = useState('');
  const [addMembersResults, setAddMembersResults] = useState([]);
  const [addMembersSelected, setAddMembersSelected] = useState([]);
  const [addMembersSearching, setAddMembersSearching] = useState(false);

  // ============ REFS ============
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentChatRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const topSentinelRef = useRef(null);

  // ============ EFFECTS ============
  useEffect(() => { resetUnreadMessages(); }, []);
  useEffect(() => { currentChatRef.current = currentChat; }, [currentChat]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setShowSidebar(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(e.target)) {
        setShowAttachmentMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🔌 SOCKET LISTENERS
  useEffect(() => {
    loadChats();

    const handleNewMessage = (message) => {
      console.log('📩 new_message:', message?._id);
      const chat = currentChatRef.current;
      if (chat && String(message.roomId) === String(chat._id)) {
        setMessages(prev => {
          if (prev.some(m => String(m._id) === String(message._id))) return prev;
          return [...prev, message];
        });
        socketService.markMessageRead(message._id, chat._id);
      }
      loadChats();
    };

    const handleNewMessageNotification = (notif) => {
      console.log('🔔 new_message_notification:', notif?.roomName);
      const chat = currentChatRef.current;
      if (chat && String(notif.roomId) === String(chat._id)) {
        loadChats();
        return;
      }
      toast.custom((t) => (
        <div
          onClick={() => {
            toast.dismiss(t.id);
            setChats(prev => {
              const found = prev.find(c => String(c._id) === String(notif.roomId));
              if (found) setCurrentChat(found);
              return prev;
            });
          }}
          style={{
            background: '#17212b', color: '#fff', padding: '0.65rem 0.9rem',
            borderRadius: '0.6rem', border: '1px solid #242f3d',
            boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', gap: '0.6rem',
            cursor: 'pointer', maxWidth: 360, minWidth: 260
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: '#5288c1',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: '0.95rem', flexShrink: 0
          }}>
            {(notif.sender?.name || notif.sender?.username || notif.roomName || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {notif.roomName || 'Chat'}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#7d8e98',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {notif.preview || 'Nuevo mensaje'}
            </div>
          </div>
        </div>
      ), { duration: 5000 });
      loadChats();
    };

    const handleUserTyping = ({ userId, roomId, isTyping }) => {
      const chat = currentChatRef.current;
      if (chat && String(roomId) === String(chat._id) && String(userId) !== String(socketService.getUserId())) {
        setTypingUsers(prev => {
          if (isTyping) return { ...prev, [userId]: true };
          const n = { ...prev };
          delete n[userId];
          return n;
        });
      }
    };

    const handleMessageRead = ({ messageId, userId }) => {
      setMessages(prev => prev.map(m =>
        String(m._id) === String(messageId)
          ? { ...m, readBy: [...(m.readBy || []), { userId }] }
          : m
      ));
    };

    const handleUserJoined = ({ roomId }) => {
      if (currentChatRef.current && String(roomId) === String(currentChatRef.current._id)) loadChats();
    };

    const handleUserLeft = ({ roomId }) => {
      if (currentChatRef.current && String(roomId) === String(currentChatRef.current._id)) loadChats();
    };

    const handleGroupUpdate = ({ roomId }) => {
      if (currentChatRef.current && String(roomId) === String(currentChatRef.current._id)) loadChats();
    };

    const handleUserStatus = ({ userId, isOnline, lastSeen }) => {
      setChats(prev => prev.map(c => {
        if (c.type === 'direct') {
          const other = c.participants?.find(p => String(p._id || p) === String(userId));
          if (other) return { ...c, otherIsOnline: isOnline, otherLastSeen: lastSeen || c.otherLastSeen };
        }
        return c;
      }));
    };

    const socket = socketService.socket;
    if (socket) {
      socket.on('new_message', handleNewMessage);
      socket.on('new_message_notification', handleNewMessageNotification);
      socket.on('user_typing', handleUserTyping);
      socket.on('message_read', handleMessageRead);
      socket.on('user_joined', handleUserJoined);
      socket.on('user_left', handleUserLeft);
      socket.on('group_members_updated', handleGroupUpdate);
      socket.on('user_status', handleUserStatus);
    }

    return () => {
      if (socket) {
        socket.off('new_message', handleNewMessage);
        socket.off('new_message_notification', handleNewMessageNotification);
        socket.off('user_typing', handleUserTyping);
        socket.off('message_read', handleMessageRead);
        socket.off('user_joined', handleUserJoined);
        socket.off('user_left', handleUserLeft);
        socket.off('group_members_updated', handleGroupUpdate);
        socket.off('user_status', handleUserStatus);
      }
    };
  }, []);

  // Unirse al chat activo
  useEffect(() => {
    if (currentChat) {
      setMessages([]);
      setMessagePage(1);
      setHasMoreMessages(false);
      loadMessages(currentChat._id, 1, true);
      socketService.joinChatRoom(currentChat._id);
      if (isMobile) setShowSidebar(false);
      return () => { socketService.leaveChatRoom(currentChat._id); };
    }
  }, [currentChat?._id]);

  useEffect(() => {
    if (messages.length > 0 && messagePage === 1 && !loadingMore) {
      scrollToBottom();
    }
  }, [messages.length]);

  useEffect(() => {
    if (!hasMoreMessages || loadingMore || !currentChat) return;
    const sentinel = topSentinelRef.current;
    const container = messagesContainerRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore) loadMoreMessages();
    }, { root: container, threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreMessages, loadingMore, currentChat?._id, messagePage]);

  // ============ API ============
  const loadChats = async () => {
    try {
      const response = await api.get('/chat/chats');
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const loadMessages = async (roomId, page = 1, reset = false) => {
    if (page === 1) setLoading(true);
    try {
      const response = await api.get(`/chat/messages/${roomId}?page=${page}&limit=${PAGE_SIZE}`);
      const fetched = response.data.messages || [];
      if (reset || page === 1) {
        setMessages(fetched);
        setHasMoreMessages(fetched.length === PAGE_SIZE);
      } else {
        const container = messagesContainerRef.current;
        const prevScrollHeight = container?.scrollHeight || 0;
        const prevScrollTop = container?.scrollTop || 0;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => String(m._id)));
          const newOld = fetched.filter(m => !existingIds.has(String(m._id)));
          return [...newOld, ...prev];
        });
        setHasMoreMessages(fetched.length === PAGE_SIZE);
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
          }
        });
      }
      socketService.markRoomRead(roomId);
      setUnreadCounts(prev => ({ ...prev, [roomId]: 0 }));
    } catch (error) {
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMessages = useCallback(async () => {
    if (!currentChat || loadingMore || !hasMoreMessages) return;
    setLoadingMore(true);
    const nextPage = messagePage + 1;
    await loadMessages(currentChat._id, nextPage, false);
    setMessagePage(nextPage);
    setLoadingMore(false);
  }, [currentChat, loadingMore, hasMoreMessages, messagePage]);

  // ✅ ENVIAR MENSAJE POR HTTP (para que dispare el push)
  const sendMessage = async () => {
    if (selectedFiles.length > 0) { uploadFiles(); return; }
    if (!messageInput.trim() || !currentChat) return;

    const text = messageInput;
    setMessageInput('');

    try {
      const res = await api.post('/chat/send', {
        roomId: currentChat._id,
        text
      });

      if (res.data?.message) {
        setMessages(prev => {
          if (prev.some(m => String(m._id) === String(res.data.message._id))) return prev;
          return [...prev, res.data.message];
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar mensaje');
      setMessageInput(text);
    }

    if (typing) { socketService.sendTyping(currentChat._id, false); setTyping(false); }
    if (inputRef.current) inputRef.current.style.height = 'auto';
  };

  const handleTyping = (e) => {
    setMessageInput(e.target.value);
    if (!typing && e.target.value.trim() && currentChat) {
      setTyping(true);
      socketService.sendTyping(currentChat._id, true);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (typing && currentChat) {
        socketService.sendTyping(currentChat._id, false);
        setTyping(false);
      }
    }, 2000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const startDirectChat = async (userId) => {
    if (!userId) return;
    setSearching(true);
    try {
      const response = await api.post('/chat/direct', { userId });
      setCurrentChat(response.data.chat);
      setShowNewChat(false); setSearchQuery(''); setSearchUsers([]);
      loadChats();
      toast.success('Chat iniciado');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear chat');
    } finally { setSearching(false); }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return toast.error('Nombre requerido');
    setSearching(true);
    try {
      const response = await api.post('/chat/group', { name: newGroupName, participants: selectedUsers });
      setCurrentChat(response.data.chat);
      setShowNewChat(false); setNewGroupName(''); setSelectedUsers([]);
      setSearchQuery(''); setSearchUsers([]); loadChats();
      toast.success('Grupo creado');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear grupo');
    } finally { setSearching(false); }
  };

  const searchUsersForChat = async () => {
    if (!searchQuery.trim()) { setSearchUsers([]); return; }
    setSearching(true);
    try {
      const response = await api.get(`/chat/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchUsers(response.data.users || []);
    } catch (error) { toast.error('Error al buscar usuarios'); }
    finally { setSearching(false); }
  };

  // ============ INFO DEL CHAT ============
  const openChatInfo = async () => {
    if (!currentChat) return;
    setShowChatInfo(true);
    setInfoTab('members');
    setLoadingInfo(true);
    try {
      const [infoRes, mediaRes] = await Promise.all([
        api.get(`/chat/rooms/${currentChat._id}/info`),
        api.get(`/chat/rooms/${currentChat._id}/media?type=all`)
      ]);
      setChatInfo(infoRes.data.room);
      const items = mediaRes.data.items || [];
      setChatMedia({
        images: items.filter(i => i.mimeType?.startsWith('image/')),
        files: items.filter(i => !i.mimeType?.startsWith('image/') && !i.mimeType?.startsWith('video/'))
      });
    } catch (e) {
      toast.error('Error al cargar info');
    } finally {
      setLoadingInfo(false);
    }
  };

  const closeChatInfo = () => {
    setShowChatInfo(false);
    setChatInfo(null);
    setChatMedia({ images: [], files: [] });
  };

  const searchUsersToAdd = async () => {
    if (!addMembersSearch.trim()) { setAddMembersResults([]); return; }
    setAddMembersSearching(true);
    try {
      const r = await api.get(`/chat/users/search?q=${encodeURIComponent(addMembersSearch)}`);
      const existingIds = (chatInfo?.participants || []).map(p => String(p._id));
      const filtered = (r.data.users || []).filter(u => !existingIds.includes(String(u._id)));
      setAddMembersResults(filtered);
    } catch { toast.error('Error al buscar'); }
    finally { setAddMembersSearching(false); }
  };

  const toggleAddMember = (id) => {
    setAddMembersSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const confirmAddMembers = async () => {
    if (!addMembersSelected.length || !currentChat) return;
    try {
      await api.post(`/chat/group/${currentChat._id}/add-members`, { userIds: addMembersSelected });
      toast.success(`${addMembersSelected.length} miembro(s) añadido(s)`);
      setShowAddMembers(false);
      setAddMembersSelected([]);
      setAddMembersSearch('');
      setAddMembersResults([]);
      const r = await api.get(`/chat/rooms/${currentChat._id}/info`);
      setChatInfo(r.data.room);
      loadChats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al añadir');
    }
  };

  const removeMember = async (userId) => {
    if (!currentChat) return;
    if (!window.confirm('¿Quitar a este miembro del grupo?')) return;
    try {
      await api.post(`/chat/group/${currentChat._id}/remove-member`, { userId });
      toast.success('Miembro quitado');
      const r = await api.get(`/chat/rooms/${currentChat._id}/info`);
      setChatInfo(r.data.room);
      loadChats();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error');
    }
  };

  // ============ ARCHIVOS ============
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newFiles = files.map(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} es demasiado grande (máx. 10MB)`);
        return null;
      }
      return {
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name, size: file.size,
        type: file.type.startsWith('image/') ? 'image' : 'file'
      };
    }).filter(Boolean);
    setSelectedFiles(prev => [...prev, ...newFiles]);
    setShowAttachmentMenu(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 || !currentChat) return;
    setUploading(true); setUploadProgress(0);
    for (let i = 0; i < selectedFiles.length; i++) {
      const fileData = selectedFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', fileData.file);
        if (i === selectedFiles.length - 1 && messageInput.trim()) {
          formData.append('caption', messageInput.trim());
        } else if (fileData.type === 'image') {
          formData.append('caption', '📷 Imagen');
        } else {
          formData.append('caption', `📎 ${fileData.name}`);
        }
        await api.post(`/chat/upload/${currentChat._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setUploadProgress(((i + 1) / selectedFiles.length) * 100);
      } catch (error) { toast.error(`Error al subir ${fileData.name}`); }
    }
    selectedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setSelectedFiles([]); setMessageInput(''); setUploadProgress(0); setUploading(false);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    toast.success('Archivos enviados');
  };

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/chat/')) {
      const filename = url.replace('/uploads/chat/', '');
      return `${API_URL}/api/chat/files/${filename}`;
    }
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return `${API_URL}${url}`;
  };

  const openImageViewer = (url, name) => setImageViewer({ open: true, url: getFileUrl(url), name });
  const closeImageViewer = () => setImageViewer({ open: false, url: '', name: '' });

  // ============ HELPERS ============
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toUpperCase() : '';
  };

  const scrollToBottom = () => {
    setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
  };

  const formatTime = (date) => {
    const d = new Date(date), now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff === 1) return 'Ayer';
    if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
  };

  const formatMessageDate = (date) => {
    const d = new Date(date), today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return `Hoy ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (d.toDateString() === yesterday.toDateString()) return `Ayer ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const formatLastSeen = (date) => {
    if (!date) return 'última vez desconocido';
    const d = new Date(date), now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    const diffHr = Math.floor((now - d) / 3600000);
    const diffDay = Math.floor((now - d) / 86400000);
    if (diffMin < 1) return 'última vez hace un momento';
    if (diffMin < 60) return `última vez hace ${diffMin} min`;
    if (diffHr < 24) return `última vez hace ${diffHr} h`;
    if (diffDay === 1) return `última vez ayer a las ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    if (diffDay < 7) return `última vez el ${d.toLocaleDateString([], { weekday: 'long' })}`;
    return `última vez el ${d.toLocaleDateString([], { day: '2-digit', month: '2-digit' })}`;
  };

  const getTypingText = () => {
    const list = Object.keys(typingUsers).filter(id => typingUsers[id]);
    if (list.length === 0) return '';
    if (list.length === 1) return 'escribiendo...';
    return `${list.length} personas escribiendo...`;
  };

  const getStatusText = (chat) => {
    if (!chat) return '';
    if (chat.type === 'group') {
      const online = chat.onlineCount || 0;
      const members = chat.memberCount || 0;
      if (online > 0) return `${online} de ${members} en línea`;
      return `${members} miembros`;
    }
    if (chat.otherIsOnline) return 'en línea';
    return formatLastSeen(chat.otherLastSeen);
  };

  const isChatOnline = (chat) => {
    if (!chat) return false;
    if (chat.type === 'group') return (chat.onlineCount || 0) > 0;
    return !!chat.otherIsOnline;
  };

  const getUserInitial = (name) => (!name ? 'U' : name[0]?.toUpperCase() || 'U');
  const getUnreadCount = (chatId) => unreadCounts[chatId] || 0;
  const filteredChats = chats.filter(chat => {
    if (!chatFilter) return true;
    return (chat.displayName || chat.name || '').toLowerCase().includes(chatFilter);
  });

  const handleChatFilter = (e) => setChatFilter(e.target.value.toLowerCase());
  const toggleSelectUserForGroup = (userId) => setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  const handleCloseNewChat = () => { setShowNewChat(false); setSearchQuery(''); setSearchUsers([]); setNewGroupName(''); setSelectedUsers([]); };
  const handleBackToSidebar = () => { setShowSidebar(true); setCurrentChat(null); };

  // ============ RENDER MENSAJE ============
  const renderMessageContent = (msg, isOwn) => {
    const hasAttachment = msg.attachments && msg.attachments.length > 0;
    return (
      <div>
        {msg.text && (
          <div style={{ fontSize: '0.95rem', wordWrap: 'break-word', whiteSpace: 'pre-wrap',
            lineHeight: 1.4, marginBottom: hasAttachment ? '0.5rem' : 0, color: TG.text }}>{msg.text}</div>
        )}
        {hasAttachment && msg.attachments.map((attachment, idx) => {
          const isAttachImage = attachment.mimeType?.startsWith('image/');
          const isAttachVideo = attachment.mimeType?.startsWith('video/');
          if (isAttachImage) {
            return (
              <div key={idx} style={{ marginTop: idx > 0 ? '0.5rem' : 0 }}>
                <div className="image-wrap" style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                  <div onClick={() => openImageViewer(attachment.url, attachment.name)}
                    style={{ cursor: 'pointer', borderRadius: '0.5rem', overflow: 'hidden',
                      background: '#000', display: 'block', maxWidth: '100%' }}>
                    <img src={getFileUrl(attachment.url)} alt={attachment.name || 'Imagen'}
                      style={{ maxWidth: '100%', maxHeight: 320, display: 'block', objectFit: 'cover' }}
                      loading="lazy" />
                  </div>
                  <div className="image-overlay" style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    padding: '1.5rem 0.5rem 0.35rem', display: 'flex',
                    justifyContent: 'flex-end', alignItems: 'center', gap: '0.25rem',
                    borderRadius: '0 0 0.5rem 0.5rem', pointerEvents: 'none'
                  }}>
                    <EyeIcon style={{ width: '0.85rem', color: '#fff' }} />
                    <span style={{ fontSize: '0.65rem', color: '#fff' }}>Ver</span>
                    <div style={{ flex: 1 }} />
                    <button onClick={(e) => { e.stopPropagation(); window.open(getFileUrl(attachment.url), '_blank'); }}
                      style={{ pointerEvents: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none',
                        borderRadius: '0.4rem', color: '#fff', cursor: 'pointer', padding: '0.2rem 0.5rem',
                        fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                      <ArrowDownTrayIcon style={{ width: '0.7rem' }} /> Descargar
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={idx} onClick={() => window.open(getFileUrl(attachment.url), '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.7rem',
                padding: '0.6rem 0.75rem', background: 'rgba(0,0,0,0.2)',
                borderRadius: '0.5rem', cursor: 'pointer', marginTop: idx > 0 ? '0.5rem' : 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: '0.5rem',
                background: isAttachVideo ? TG.danger : TG.accentDark,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {isAttachVideo ? <PlayIcon style={{ width: '1.1rem', color: '#fff' }} />
                               : <DocumentIcon style={{ width: '1.1rem', color: '#fff' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: TG.text }}>
                  {attachment.name}
                </div>
                <div style={{ fontSize: '0.7rem', color: TG.textMuted, marginTop: 2 }}>
                  {formatFileSize(attachment.size)} • {getFileExtension(attachment.name)}
                </div>
              </div>
              <ArrowDownTrayIcon style={{ width: '1rem', color: TG.textMuted }} />
            </div>
          );
        })}
      </div>
    );
  };

  // ============ JSX ============
  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', background: TG.bg, color: TG.text,
      overflow: 'hidden', position: 'relative',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* VISOR IMAGEN */}
      {imageViewer.open && (
        <div onClick={closeImageViewer} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
          zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', cursor: 'pointer' }}>
          <button onClick={closeImageViewer} style={{ position: 'absolute', top: '1rem', right: '1rem',
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', color: '#fff',
            width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <XMarkIcon style={{ width: '1.5rem' }} />
          </button>
          <div style={{ textAlign: 'center', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={imageViewer.url} alt={imageViewer.name} onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '0.75rem',
                boxShadow: '0 20px 60px rgba(0,0,0,0.8)', objectFit: 'contain' }} />
            <div style={{ marginTop: '1rem', color: TG.textMuted, fontSize: '0.85rem' }}>{imageViewer.name}</div>
          </div>
        </div>
      )}

      {/* SIDEBAR */}
      <div style={{
        width: isMobile ? '100%' : '380px', minWidth: isMobile ? '100%' : '380px',
        maxWidth: isMobile ? '100%' : '380px', borderRight: `1px solid ${TG.border}`,
        display: isMobile && !showSidebar ? 'none' : 'flex', flexDirection: 'column',
        background: TG.sidebar, overflow: 'hidden',
        position: isMobile ? 'absolute' : 'relative', zIndex: 20, height: '100%'
      }}>
        <div style={{ padding: '0.6rem 1rem', borderBottom: `1px solid ${TG.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: TG.accentDark,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChatBubbleLeftRightIcon style={{ width: '1.1rem', color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: TG.text }}>Chats</h2>
            </div>
            <button onClick={() => { setShowNewChat(true); setSearchQuery(''); setSearchUsers([]);
              setNewGroupName(''); setSelectedUsers([]); setActiveTab('direct'); }}
              style={{ padding: '0.4rem', background: 'transparent', border: 'none',
                borderRadius: '50%', color: TG.textMuted, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlusIcon style={{ width: '1.2rem' }} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', background: TG.input, borderRadius: '0.6rem',
            padding: '0.5rem 0.75rem', border: `1px solid ${TG.divider}`, alignItems: 'center' }}>
            <MagnifyingGlassIcon style={{ width: '0.95rem', color: TG.textMuted }} />
            <input type="text" placeholder="Buscar" value={chatFilter} onChange={handleChatFilter}
              style={{ flex: 1, background: 'transparent', border: 'none', color: TG.text,
                fontSize: '0.875rem', outline: 'none' }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.3rem 0', minHeight: 0 }}>
          {loadingChats ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loader" /></div>
          ) : filteredChats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: TG.textMuted }}>
              <ChatBubbleLeftRightIcon style={{ width: '2.5rem', margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontSize: '0.85rem' }}>{chatFilter ? 'Sin resultados' : 'No hay chats'}</p>
            </div>
          ) : filteredChats.map(chat => {
            const active = currentChat?._id === chat._id;
            return (
              <div key={chat._id} onClick={() => setCurrentChat(chat)}
                style={{ padding: '0.55rem 1rem', cursor: 'pointer',
                  background: active ? TG.accentDark : 'transparent', transition: 'background 0.1s' }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = TG.hover; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                  <div style={{ position: 'relative', width: 46, height: 46, minWidth: 46 }}>
                    <div style={{ width: 46, height: 46, borderRadius: '50%',
                      background: TG.accentDark, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {chat.type === 'group' ? <UserGroupIcon style={{ width: '1.3rem', color: '#fff' }} />
                        : <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff' }}>{getUserInitial(chat.displayName)}</span>}
                    </div>
                    {isChatOnline(chat) && (
                      <div style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12,
                        background: TG.online, border: `2px solid ${TG.sidebar}`, borderRadius: '50%' }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: TG.text, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
                        {chat.displayName || chat.name}
                      </div>
                      {chat.lastMessage?.timestamp && (
                        <div style={{ fontSize: '0.7rem', color: active ? 'rgba(255,255,255,0.75)' : TG.textMuted }}>
                          {formatTime(chat.lastMessage.timestamp)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '0.8rem', color: active ? 'rgba(255,255,255,0.85)' : TG.textMuted,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }}>
                        {chat.lastMessage?.text || 'Sin mensajes'}
                      </div>
                      {getUnreadCount(chat._id) > 0 && (
                        <div style={{ background: TG.accent, color: '#fff',
                          fontSize: '0.7rem', fontWeight: 600, padding: '0.1rem 0.45rem',
                          borderRadius: '1rem', minWidth: 20, textAlign: 'center' }}>{getUnreadCount(chat._id)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isMobile && showSidebar && (
        <div onClick={() => setShowSidebar(false)} style={{ position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 19 }} />
      )}

      {/* ÁREA PRINCIPAL */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: TG.bg,
        overflow: 'hidden', minWidth: 0, height: '100%' }}>
        {!currentChat ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: TG.textMuted, textAlign: 'center', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: TG.accentDark,
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <ChatBubbleLeftRightIcon style={{ width: '2rem', color: '#fff', opacity: 0.7 }} />
              </div>
              <h3 style={{ color: TG.text, fontSize: '1.1rem', marginBottom: '0.4rem' }}>Selecciona un chat</h3>
              <p style={{ fontSize: '0.85rem' }}>o inicia una nueva conversación</p>
              {isMobile && <button onClick={handleBackToSidebar}
                style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', background: TG.accentDark,
                  border: 'none', borderRadius: '1rem', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Ver Chats</button>}
            </div>
            <button onClick={() => window.location.href = '/'}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 1rem',
                background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '1rem',
                color: TG.textMuted, cursor: 'pointer', fontSize: '0.85rem' }}>
              <HomeIcon style={{ width: '1rem' }} /> Volver al Dashboard
            </button>
          </div>
        ) : (
          <>
            {/* HEADER CHAT - CLICKEABLE */}
            <div style={{ padding: '0.5rem 1rem', borderBottom: `1px solid ${TG.border}`,
              display: 'flex', alignItems: 'center', gap: '0.75rem', background: TG.header, flexShrink: 0 }}>
              {isMobile && <button onClick={handleBackToSidebar}
                style={{ background: 'none', border: 'none', color: TG.textMuted, cursor: 'pointer' }}>
                <ChevronLeftIcon style={{ width: '1.4rem' }} /></button>}
              <div onClick={openChatInfo} style={{ width: 40, height: 40, borderRadius: '50%', background: TG.accentDark,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
                {currentChat.type === 'group' ? <UserGroupIcon style={{ width: '1.2rem', color: '#fff' }} />
                  : <span style={{ fontSize: '1rem', fontWeight: 600, color: '#fff' }}>{getUserInitial(currentChat.displayName || currentChat.name)}</span>}
              </div>
              <div onClick={openChatInfo} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                <div style={{ fontWeight: 600, fontSize: '0.95rem', color: TG.text, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentChat.displayName || currentChat.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: getTypingText() ? TG.accent : isChatOnline(currentChat) ? TG.online : TG.textMuted }}>
                  {getTypingText() || getStatusText(currentChat)}
                </div>
              </div>
              <button onClick={openChatInfo}
                style={{ background: 'transparent', border: 'none', color: TG.textMuted, cursor: 'pointer', padding: '0.3rem' }}>
                <EyeIcon style={{ width: '1.2rem' }} />
              </button>
            </div>

            {/* MENSAJES */}
            <div ref={messagesContainerRef} style={{
              flex: 1, overflowY: 'auto', padding: '0.75rem 1rem', minHeight: 0,
              background: TG.bg,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}>

              {hasMoreMessages && (
                <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                  {loadingMore ? (
                    <div className="loader" style={{ width: 24, height: 24, margin: '0 auto' }} />
                  ) : (
                    <button onClick={loadMoreMessages} style={{ padding: '0.35rem 0.85rem',
                      background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '1rem',
                      color: TG.accent, cursor: 'pointer', fontSize: '0.75rem',
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      <ArrowUpIcon style={{ width: '0.85rem' }} /> Cargar mensajes anteriores
                    </button>
                  )}
                </div>
              )}
              {!hasMoreMessages && messages.length > 0 && (
                <div style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '0.7rem', color: TG.textMuted }}>
                  — No hay más mensajes —
                </div>
              )}
              <div ref={topSentinelRef} style={{ height: 1 }} />

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}><div className="loader" /></div>
              ) : messages.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: '100%', color: TG.textMuted, textAlign: 'center' }}>
                  <div>
                    <ChatBubbleLeftRightIcon style={{ width: '2.5rem', margin: '0 auto 0.75rem', opacity: 0.4 }} />
                    <p style={{ fontSize: '0.9rem' }}>No hay mensajes aún</p>
                    <p style={{ fontSize: '0.75rem', opacity: 0.7 }}>Envía un mensaje para comenzar</p>
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => {
                    const isOwn = String(msg.senderId?._id || msg.senderId) === String(socketService.getUserId());
                    const prevMsg = messages[idx - 1];
                    const showDate = !prevMsg || formatMessageDate(msg.createdAt) !== formatMessageDate(prevMsg.createdAt);
                    return (
                      <React.Fragment key={msg._id || idx}>
                        {showDate && (
                          <div style={{ textAlign: 'center', margin: '1rem 0 0.6rem' }}>
                            <div style={{ display: 'inline-block', background: TG.dateBadge,
                              padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.7rem',
                              color: TG.textMuted, fontWeight: 500, backdropFilter: 'blur(4px)' }}>
                              {formatMessageDate(msg.createdAt)}
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start',
                          marginBottom: '0.25rem', paddingLeft: isOwn ? '3rem' : 0, paddingRight: isOwn ? 0 : '3rem' }}>
                          <div style={{ maxWidth: isMobile ? '85%' : '65%' }}>
                            <div style={{ padding: msg.type === 'system' ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                              borderRadius: isOwn ? '0.75rem 0.75rem 0.15rem 0.75rem' : '0.75rem 0.75rem 0.75rem 0.15rem',
                              background: msg.type === 'system' ? 'transparent' : isOwn ? TG.bubbleOwn : TG.bubbleOther,
                              boxShadow: msg.type === 'system' ? 'none' : '0 1px 2px rgba(0,0,0,0.2)',
                              textAlign: msg.type === 'system' ? 'center' : 'left', color: TG.text }}>
                              {msg.type === 'system' ? (
                                <span style={{ fontSize: '0.75rem', color: TG.textMuted, fontStyle: 'italic' }}>{msg.text}</span>
                              ) : (
                                <>
                                  {!isOwn && currentChat.type === 'group' && (
                                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: TG.accent, marginBottom: 3 }}>
                                      {msg.senderId?.name || msg.senderId?.username || 'Usuario'}
                                    </div>
                                  )}
                                  {renderMessageContent(msg, isOwn)}
                                </>
                              )}
                              {msg.type !== 'system' && (
                                <div style={{ fontSize: '0.65rem', textAlign: 'right', marginTop: '0.2rem',
                                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem',
                                  color: 'rgba(255,255,255,0.6)' }}>
                                  {formatTime(msg.createdAt)}
                                  {isOwn && (
                                    <CheckCircleIcon style={{ width: '0.7rem',
                                      color: msg.readBy?.length > 0 ? TG.accent : 'rgba(255,255,255,0.5)' }} />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {getTypingText() && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem', paddingRight: '3rem' }}>
                      <div style={{ background: TG.bubbleOther, padding: '0.5rem 0.85rem',
                        borderRadius: '0.75rem 0.75rem 0.75rem 0.15rem',
                        fontSize: '0.8rem', color: TG.textMuted,
                        display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span className="typing-dots"><span>.</span><span>.</span><span>.</span></span>
                        {getTypingText()}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* INPUT */}
            <div style={{ padding: '0.5rem 1rem', borderTop: `1px solid ${TG.border}`, background: TG.header, flexShrink: 0 }}>
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '0.6rem', padding: '0.6rem', background: TG.sidebar,
                  borderRadius: '0.75rem', border: `1px solid ${TG.divider}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', color: TG.textMuted, fontWeight: 600 }}>
                      {selectedFiles.length} archivo(s)</span>
                    <button onClick={() => { selectedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
                      setSelectedFiles([]); }} style={{ background: 'none', border: 'none',
                      color: TG.accent, cursor: 'pointer', fontSize: '0.75rem' }}>Cancelar</button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
                    {selectedFiles.map((fileData, idx) => (
                      <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                        {fileData.preview ? (
                          <div style={{ width: 64, height: 64, borderRadius: '0.5rem', overflow: 'hidden', background: '#000' }}>
                            <img src={fileData.preview} alt={fileData.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        ) : (
                          <div style={{ width: 64, height: 64, borderRadius: '0.5rem', background: TG.input,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                            <DocumentIcon style={{ width: '1.3rem', color: TG.accent }} />
                            <span style={{ fontSize: '0.5rem', color: TG.textMuted }}>{getFileExtension(fileData.name)}</span>
                          </div>
                        )}
                        <button onClick={() => removeSelectedFile(idx)}
                          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                            borderRadius: '50%', background: TG.danger, border: `2px solid ${TG.sidebar}`,
                            color: '#fff', cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.6rem', fontWeight: 700 }}>×</button>
                      </div>
                    ))}
                  </div>
                  {uploading && (
                    <div style={{ marginTop: '0.5rem', background: TG.input, borderRadius: 4, height: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: TG.accent,
                        width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input type="text" value={messageInput} onChange={handleTyping}
                      placeholder="Añade un mensaje..."
                      style={{ flex: 1, padding: '0.5rem 0.85rem', background: TG.input,
                        border: 'none', borderRadius: '1rem', color: TG.text,
                        outline: 'none', fontSize: '0.85rem' }} />
                    <button onClick={uploadFiles} disabled={uploading}
                      style={{ padding: '0.5rem 1rem', background: uploading ? TG.divider : TG.accent,
                        border: 'none', borderRadius: '1rem', color: '#fff',
                        fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}>
                      {uploading ? `${Math.round(uploadProgress)}%` : 'Enviar'}
                    </button>
                  </div>
                </div>
              )}

              {selectedFiles.length === 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div ref={attachmentMenuRef} style={{ position: 'relative' }}>
                    <button onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                      style={{ width: 40, height: 40, padding: '0.5rem', background: 'transparent',
                        border: 'none', borderRadius: '50%', color: TG.textMuted,
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0 }}>
                      <PlusIcon style={{ width: '1.25rem' }} />
                    </button>
                    {showAttachmentMenu && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '0.5rem',
                        background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '0.75rem',
                        padding: '0.5rem', display: 'flex', gap: '0.3rem', zIndex: 10,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.6)' }}>
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect}
                          accept="image/*" multiple style={{ display: 'none' }} id="img-chat" />
                        <label htmlFor="img-chat" style={{ display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem',
                          borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.7rem', color: TG.text }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: TG.input,
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PhotoIcon style={{ width: '1.1rem', color: TG.accent }} />
                          </div>Galería
                        </label>
                        <input type="file" ref={fileInputRef} onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                          multiple style={{ display: 'none' }} id="file-chat" />
                        <label htmlFor="file-chat" style={{ display: 'flex', flexDirection: 'column',
                          alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem',
                          borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.7rem', color: TG.text }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: TG.input,
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DocumentIcon style={{ width: '1.1rem', color: TG.accent }} />
                          </div>Archivo
                        </label>
                      </div>
                    )}
                  </div>
                  <textarea ref={inputRef} value={messageInput} onChange={handleTyping}
                    onKeyPress={handleKeyPress} placeholder="Escribe un mensaje..." rows={1}
                    style={{ flex: 1, padding: '0.6rem 1rem', background: TG.input,
                      border: 'none', borderRadius: '1.25rem', color: TG.text,
                      outline: 'none', fontSize: '0.9rem', resize: 'none',
                      fontFamily: 'inherit', maxHeight: 100, overflowY: 'auto', lineHeight: 1.4 }}
                    onInput={(e) => { e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }} />
                  <button onClick={sendMessage} disabled={!messageInput.trim()}
                    style={{ width: 40, height: 40, minWidth: 40, padding: '0.5rem',
                      background: messageInput.trim() ? TG.accent : 'transparent',
                      border: 'none', borderRadius: '50%',
                      color: messageInput.trim() ? '#fff' : TG.textMuted,
                      cursor: messageInput.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <PaperAirplaneIcon style={{ width: '1.2rem', transform: 'rotate(-45deg)' }} />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* MODAL INFO DEL CHAT */}
      {showChatInfo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '0.75rem',
            width: '100%', maxWidth: 460, maxHeight: '88vh', display: 'flex',
            flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '0.9rem 1rem', borderBottom: `1px solid ${TG.divider}`,
              display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
              <button onClick={closeChatInfo}
                style={{ background: 'transparent', border: 'none', color: TG.textMuted, cursor: 'pointer', padding: 0 }}>
                <XMarkIcon style={{ width: '1.2rem' }} />
              </button>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: TG.text, flex: 1 }}>Info del chat</h3>
            </div>

            {loadingInfo ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}><div className="loader" /></div>
            ) : chatInfo ? (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ textAlign: 'center', padding: '1.5rem 1rem 1rem' }}>
                  <div style={{ width: 84, height: 84, borderRadius: '50%',
                    background: TG.accentDark, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', margin: '0 auto 0.75rem' }}>
                    {chatInfo.type === 'group'
                      ? <UserGroupIcon style={{ width: '2.2rem', color: '#fff' }} />
                      : <span style={{ fontSize: '2rem', fontWeight: 600, color: '#fff' }}>
                          {getUserInitial(chatInfo.participants.find(p => String(p._id) !== String(socketService.getUserId()))?.name || currentChat.displayName)}
                        </span>}
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 600, color: TG.text }}>
                    {chatInfo.name || currentChat.displayName}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: TG.textMuted, marginTop: 4 }}>
                    {chatInfo.type === 'group'
                      ? `${chatInfo.participants.length} miembros`
                      : (chatInfo.participants.find(p => String(p._id) !== String(socketService.getUserId()))?.isOnline
                          ? 'en línea' : 'última vez recientemente')}
                  </div>
                </div>

                <div style={{ display: 'flex', borderBottom: `1px solid ${TG.divider}` }}>
                  {[
                    { id: 'members', label: 'Miembros' },
                    { id: 'media', label: 'Fotos' },
                    { id: 'files', label: 'Archivos' }
                  ].map(t => (
                    <button key={t.id} onClick={() => setInfoTab(t.id)}
                      style={{ flex: 1, padding: '0.65rem', background: 'transparent',
                        border: 'none', borderBottom: `2px solid ${infoTab === t.id ? TG.accent : 'transparent'}`,
                        color: infoTab === t.id ? TG.accent : TG.textMuted,
                        cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      {t.label}
                    </button>
                  ))}
                </div>

                <div style={{ padding: '0.75rem' }}>
                  {infoTab === 'members' && (
                    <div>
                      {chatInfo.type === 'group' && (
                        <button onClick={() => { setShowAddMembers(true); setAddMembersSearch(''); setAddMembersSelected([]); setAddMembersResults([]); }}
                          style={{ width: '100%', padding: '0.65rem', marginBottom: '0.6rem',
                            background: TG.accentDark, border: 'none', borderRadius: '0.5rem',
                            color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <PlusIcon style={{ width: '1rem' }} /> Añadir miembros
                        </button>
                      )}
                      {chatInfo.participants.map(p => {
                        const isMe = String(p._id) === String(socketService.getUserId());
                        const isAdmin = (chatInfo.admins || []).some(a => String(a._id || a) === String(p._id));
                        return (
                          <div key={p._id} style={{ display: 'flex', alignItems: 'center',
                            gap: '0.7rem', padding: '0.55rem 0.5rem', borderRadius: '0.5rem' }}>
                            <div style={{ position: 'relative', width: 40, height: 40 }}>
                              <div style={{ width: 40, height: 40, borderRadius: '50%',
                                background: TG.accentDark, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', color: '#fff', fontWeight: 600 }}>
                                {getUserInitial(p.name || p.username)}
                              </div>
                              {p.isOnline && (
                                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 11, height: 11,
                                  background: TG.online, border: `2px solid ${TG.sidebar}`, borderRadius: '50%' }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.85rem', color: TG.text, fontWeight: 500 }}>
                                {p.name || p.username} {isMe && <span style={{ color: TG.textMuted, fontSize: '0.7rem' }}>(tú)</span>}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: p.isOnline ? TG.online : TG.textMuted }}>
                                {isAdmin ? 'admin · ' : ''}{p.isOnline ? 'en línea' : (p.lastSeen ? formatLastSeen(p.lastSeen) : 'offline')}
                              </div>
                            </div>
                            {chatInfo.type === 'group' && !isMe && isAdmin && (
                              <button onClick={() => removeMember(p._id)}
                                style={{ background: 'transparent', border: 'none', color: TG.danger,
                                  cursor: 'pointer', fontSize: '0.72rem' }}>Quitar</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {infoTab === 'media' && (
                    <div>
                      {chatMedia.images.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: TG.textMuted, fontSize: '0.8rem' }}>
                          No hay fotos compartidas
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                          {chatMedia.images.map((img, idx) => (
                            <div key={idx} onClick={() => openImageViewer(img.url, img.name)}
                              style={{ paddingBottom: '100%', position: 'relative',
                                background: '#000', borderRadius: '0.3rem', overflow: 'hidden', cursor: 'pointer' }}>
                              <img src={getFileUrl(img.url)} alt={img.name}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {infoTab === 'files' && (
                    <div>
                      {chatMedia.files.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: TG.textMuted, fontSize: '0.8rem' }}>
                          No hay archivos compartidos
                        </div>
                      ) : chatMedia.files.map((f, idx) => (
                        <div key={idx} onClick={() => window.open(getFileUrl(f.url), '_blank')}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem',
                            padding: '0.6rem 0.5rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = TG.hover}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <div style={{ width: 36, height: 36, borderRadius: '0.4rem', background: TG.accentDark,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <DocumentIcon style={{ width: '1rem', color: '#fff' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.8rem', color: TG.text, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                            <div style={{ fontSize: '0.68rem', color: TG.textMuted }}>
                              {formatFileSize(f.size)} • {new Date(f.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <ArrowDownTrayIcon style={{ width: '0.95rem', color: TG.textMuted }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* MODAL AÑADIR MIEMBROS */}
      {showAddMembers && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '0.75rem',
            width: '100%', maxWidth: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '0.9rem 1rem', borderBottom: `1px solid ${TG.divider}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: TG.text }}>Añadir miembros</h3>
              <button onClick={() => setShowAddMembers(false)}
                style={{ background: 'transparent', border: 'none', color: TG.textMuted, cursor: 'pointer', padding: 0 }}>
                <XMarkIcon style={{ width: '1.2rem' }} />
              </button>
            </div>
            <div style={{ padding: '0.75rem 1rem' }}>
              <div style={{ display: 'flex', gap: '0.4rem', background: TG.input, borderRadius: '0.5rem',
                padding: '0.45rem 0.75rem', border: `1px solid ${TG.divider}` }}>
                <MagnifyingGlassIcon style={{ width: '0.9rem', color: TG.textMuted }} />
                <input type="text" value={addMembersSearch}
                  onChange={(e) => setAddMembersSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsersToAdd()}
                  placeholder="Buscar usuario..." autoFocus
                  style={{ flex: 1, background: 'transparent', border: 'none', color: TG.text,
                    fontSize: '0.85rem', outline: 'none' }} />
                <button onClick={searchUsersToAdd}
                  disabled={addMembersSearching || !addMembersSearch.trim()}
                  style={{ padding: '0.25rem 0.6rem',
                    background: (!addMembersSearching && addMembersSearch.trim()) ? TG.accentDark : TG.divider,
                    border: 'none', borderRadius: '0.4rem',
                    color: (!addMembersSearching && addMembersSearch.trim()) ? '#fff' : TG.textMuted,
                    fontSize: '0.72rem',
                    cursor: (!addMembersSearching && addMembersSearch.trim()) ? 'pointer' : 'not-allowed' }}>
                  {addMembersSearching ? '...' : 'Buscar'}
                </button>
              </div>
              {addMembersSelected.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: TG.accent, marginTop: '0.5rem' }}>
                  {addMembersSelected.length} seleccionado(s)
                </div>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 0.75rem 0.75rem' }}>
              {addMembersResults.map(u => {
                const sel = addMembersSelected.includes(u._id);
                return (
                  <div key={u._id} onClick={() => toggleAddMember(u._id)}
                    style={{ padding: '0.55rem 0.5rem', borderRadius: '0.5rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                      background: sel ? TG.hover : 'transparent' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: TG.accentDark,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600 }}>
                      {getUserInitial(u.name || u.username)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', color: TG.text }}>{u.name || u.username}</div>
                      <div style={{ fontSize: '0.72rem', color: TG.textMuted }}>@{u.username}</div>
                    </div>
                    {sel && <CheckCircleIcon style={{ width: '1.1rem', color: TG.accent }} />}
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '0.75rem', borderTop: `1px solid ${TG.divider}` }}>
              <button onClick={confirmAddMembers}
                disabled={!addMembersSelected.length}
                style={{ width: '100%', padding: '0.65rem',
                  background: addMembersSelected.length ? TG.accentDark : TG.divider,
                  border: 'none', borderRadius: '0.5rem',
                  color: addMembersSelected.length ? '#fff' : TG.textMuted,
                  fontWeight: 600, cursor: addMembersSelected.length ? 'pointer' : 'not-allowed',
                  fontSize: '0.85rem' }}>
                Añadir {addMembersSelected.length || ''} miembro(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO CHAT */}
      {showNewChat && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: TG.sidebar, border: `1px solid ${TG.divider}`, borderRadius: '0.75rem',
            width: '100%', maxWidth: 500, maxHeight: '80vh', display: 'flex',
            flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '0.9rem 1rem', borderBottom: `1px solid ${TG.divider}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: TG.text }}>Nuevo Chat</h3>
              <button onClick={handleCloseNewChat}
                style={{ background: 'transparent', border: 'none', borderRadius: '50%',
                  padding: '0.3rem', cursor: 'pointer', display: 'flex' }}>
                <XMarkIcon style={{ width: '1.2rem', color: TG.textMuted }} />
              </button>
            </div>
            <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem', background: TG.input,
                borderRadius: '0.5rem', padding: '0.2rem' }}>
                <button onClick={() => setActiveTab('direct')}
                  style={{ flex: 1, padding: '0.5rem', background: activeTab === 'direct' ? TG.accentDark : 'transparent',
                    border: 'none', borderRadius: '0.4rem', color: activeTab === 'direct' ? '#fff' : TG.textMuted,
                    fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                  <UsersIcon style={{ width: '1rem', display: 'inline', marginRight: '0.3rem', verticalAlign: 'middle' }} /> Directo
                </button>
                <button onClick={() => setActiveTab('group')}
                  style={{ flex: 1, padding: '0.5rem', background: activeTab === 'group' ? TG.accentDark : 'transparent',
                    border: 'none', borderRadius: '0.4rem', color: activeTab === 'group' ? '#fff' : TG.textMuted,
                    fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                  <UserGroupIcon style={{ width: '1rem', display: 'inline', marginRight: '0.3rem', verticalAlign: 'middle' }} /> Grupo
                </button>
              </div>

              {activeTab === 'direct' ? (
                <div>
                  <div style={{ display: 'flex', gap: '0.4rem', background: TG.input, borderRadius: '0.5rem',
                    padding: '0.45rem 0.75rem', border: `1px solid ${TG.divider}`, marginBottom: '0.75rem' }}>
                    <MagnifyingGlassIcon style={{ width: '0.95rem', color: TG.textMuted }} />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchUsersForChat()}
                      placeholder="Buscar usuario..." autoFocus
                      style={{ flex: 1, background: 'transparent', border: 'none', color: TG.text,
                        fontSize: '0.875rem', outline: 'none' }} />
                    <button onClick={searchUsersForChat} disabled={searching || !searchQuery.trim()}
                      style={{ padding: '0.25rem 0.65rem',
                        background: (!searching && searchQuery.trim()) ? TG.accentDark : TG.divider,
                        border: 'none', borderRadius: '0.4rem',
                        color: (!searching && searchQuery.trim()) ? '#fff' : TG.textMuted,
                        fontSize: '0.75rem', cursor: (!searching && searchQuery.trim()) ? 'pointer' : 'not-allowed' }}>
                      {searching ? '...' : 'Buscar'}
                    </button>
                  </div>
                  {!searching && searchUsers.map(user => (
                    <div key={user._id} onClick={() => startDirectChat(user._id)}
                      style={{ padding: '0.65rem 0.75rem', marginTop: '0.4rem', background: TG.input,
                        borderRadius: '0.5rem', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: '0.7rem' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = TG.hover}
                      onMouseLeave={(e) => e.currentTarget.style.background = TG.input}>
                      <div style={{ width: 42, height: 42, borderRadius: '50%', background: TG.accentDark,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff' }}>
                          {getUserInitial(user.name || user.username)}</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: TG.text, fontSize: '0.9rem' }}>{user.name || user.username}</div>
                        <div style={{ fontSize: '0.75rem', color: TG.textMuted }}>@{user.username}</div>
                      </div>
                      <button style={{ padding: '0.35rem 0.75rem', background: TG.accent, border: 'none',
                        borderRadius: '0.4rem', color: '#fff', fontWeight: 600,
                        cursor: 'pointer', fontSize: '0.75rem' }}>Chat</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Nombre del grupo" autoFocus
                    style={{ width: '100%', padding: '0.65rem 0.85rem', background: TG.input,
                      border: `1px solid ${TG.divider}`, borderRadius: '0.5rem', color: TG.text,
                      marginBottom: '0.75rem', fontSize: '0.875rem', outline: 'none' }} />
                  <div style={{ display: 'flex', gap: '0.4rem', background: TG.input, borderRadius: '0.5rem',
                    padding: '0.45rem 0.75rem', border: `1px solid ${TG.divider}`, marginBottom: '0.75rem' }}>
                    <MagnifyingGlassIcon style={{ width: '0.95rem', color: TG.textMuted }} />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchUsersForChat()}
                      placeholder="Buscar usuarios..."
                      style={{ flex: 1, background: 'transparent', border: 'none', color: TG.text,
                        fontSize: '0.875rem', outline: 'none' }} />
                    <button onClick={searchUsersForChat} disabled={searching || !searchQuery.trim()}
                      style={{ padding: '0.25rem 0.65rem',
                        background: (!searching && searchQuery.trim()) ? TG.accentDark : TG.divider,
                        border: 'none', borderRadius: '0.4rem',
                        color: (!searching && searchQuery.trim()) ? '#fff' : TG.textMuted,
                        fontSize: '0.75rem', cursor: (!searching && searchQuery.trim()) ? 'pointer' : 'not-allowed' }}>
                      {searching ? '...' : 'Buscar'}
                    </button>
                  </div>
                  {selectedUsers.length > 0 && (
                    <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.65rem', background: TG.input,
                      borderRadius: '0.5rem' }}>
                      <div style={{ fontSize: '0.7rem', color: TG.textMuted, marginBottom: '0.35rem' }}>
                        Seleccionados ({selectedUsers.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {selectedUsers.map(userId => {
                          const user = searchUsers.find(u => u._id === userId);
                          if (!user) return null;
                          return (
                            <div key={userId} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem',
                              background: TG.accentDark, padding: '0.15rem 0.5rem', borderRadius: '1rem' }}>
                              <span style={{ fontSize: '0.75rem', color: '#fff' }}>{user.name || user.username}</span>
                              <button onClick={() => toggleSelectUserForGroup(userId)}
                                style={{ background: 'none', border: 'none', color: '#fff',
                                  cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>×</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!searching && searchUsers.map(user => {
                    const isSelected = selectedUsers.includes(user._id);
                    return (
                      <div key={user._id} onClick={() => toggleSelectUserForGroup(user._id)}
                        style={{ padding: '0.65rem 0.75rem', marginTop: '0.4rem',
                          background: isSelected ? TG.hover : TG.input,
                          borderRadius: '0.5rem', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', gap: '0.7rem' }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', background: TG.accentDark,
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#fff' }}>
                            {getUserInitial(user.name || user.username)}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: TG.text, fontSize: '0.9rem' }}>{user.name || user.username}</div>
                          <div style={{ fontSize: '0.75rem', color: TG.textMuted }}>@{user.username}</div>
                        </div>
                        {isSelected && <CheckCircleIcon style={{ width: '1.2rem', color: TG.accent }} />}
                      </div>
                    );
                  })}
                  <button onClick={createGroup} disabled={!newGroupName.trim() || selectedUsers.length === 0 || searching}
                    style={{ width: '100%', padding: '0.7rem', marginTop: '1rem',
                      background: (newGroupName.trim() && selectedUsers.length > 0 && !searching) ? TG.accentDark : TG.divider,
                      border: 'none', borderRadius: '0.5rem',
                      color: (newGroupName.trim() && selectedUsers.length > 0 && !searching) ? '#fff' : TG.textMuted,
                      fontWeight: 600, fontSize: '0.85rem',
                      cursor: (newGroupName.trim() && selectedUsers.length > 0 && !searching) ? 'pointer' : 'not-allowed' }}>
                    <UserGroupIcon style={{ width: '1rem', display: 'inline', marginRight: '0.4rem', verticalAlign: 'middle' }} />
                    Crear Grupo
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ESTILOS */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .loader { width: 28px; height: 28px; border: 2px solid ${TG.divider}; border-top-color: ${TG.accent};
                  border-radius: 50%; animation: spin 0.8s linear infinite; }
        .typing-dots span { animation: blink 1.4s infinite both; font-size: 1.4rem;
                            font-weight: bold; line-height: 0.5; }
        .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes blink { 0% { opacity: 0.2; } 20% { opacity: 1; } 100% { opacity: 0.2; } }
        .image-overlay { opacity: 0; transition: opacity 0.15s; }
        .image-wrap:hover .image-overlay { opacity: 1; }
        body { background: ${TG.bg}; }
        * { box-sizing: border-box; }
        div::-webkit-scrollbar { width: 6px; }
        div::-webkit-scrollbar-track { background: transparent; }
        div::-webkit-scrollbar-thumb { background: #2c3845; border-radius: 3px; }
        div::-webkit-scrollbar-thumb:hover { background: #3e4c5a; }
        textarea::-webkit-scrollbar { width: 4px; }
        textarea::-webkit-scrollbar-thumb { background: ${TG.accent}; border-radius: 3px; }
        input::placeholder, textarea::placeholder { color: ${TG.textMuted}; }
        @media (max-width: 768px) {
          div::-webkit-scrollbar { width: 3px; }
        }
      `}</style>
    </div>
  );
}

export default ChatApp;